import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  readonly className?: string;
  readonly invalid?: boolean;
  readonly iconLeft?: ReactNode;
  readonly iconRight?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, iconLeft, iconRight, type = 'text', ...rest },
  ref,
) {
  const inputClass = ['input', invalid ? 'input-invalid' : '', className ?? ''].filter(Boolean).join(' ');

  if (!iconLeft && !iconRight) {
    return <input ref={ref} type={type} className={inputClass} aria-invalid={invalid || undefined} {...rest} />;
  }

  const groupClass = ['input-group', iconLeft ? 'has-left' : '', iconRight ? 'has-right' : ''].filter(Boolean).join(' ');
  return (
    <div className={groupClass}>
      {iconLeft ? <span className="icon-left">{iconLeft}</span> : null}
      <input ref={ref} type={type} className={inputClass} aria-invalid={invalid || undefined} {...rest} />
      {iconRight ? typeof iconRight === 'string'
        ? <span className="icon-right">{iconRight}</span>
        : iconRight
      : null}
    </div>
  );
});

export interface FieldProps {
  readonly label?: ReactNode;
  readonly help?: ReactNode;
  readonly error?: ReactNode;
  readonly htmlFor?: string;
  readonly children: ReactNode;
}

/** Wrapper that adds a label + help / error footer to a form input. */
export function Field({ label, help, error, htmlFor, children }: FieldProps): JSX.Element {
  return (
    <div className="field">
      {label ? (
        <label className="field-label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : null}
      {children}
      {error ? <div className="field-error" role="alert">{error}</div> : help ? <div className="field-help">{help}</div> : null}
    </div>
  );
}
